import { Outlet, Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  ClipboardDocumentListIcon,
  ServerIcon,
  Cog6ToothIcon,
  ExclamationTriangleIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  CircleStackIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/react/24/outline';
import { Menu, MenuButton, MenuItem, MenuItems, Transition, Dialog, DialogPanel, TransitionChild } from '@headlessui/react';
import { Fragment, useState } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { ElasticSheetProvider, useElasticSheet } from '../../lib/useElasticSheet';

const navigation = [
  { name: '仪表盘', href: '/', icon: HomeIcon },
  { name: '任务管理', href: '/tasks', icon: ClipboardDocumentListIcon },
  { name: '实例监控', href: '/instances', icon: ServerIcon },
  { name: '缓存管理', href: '/cache', icon: CircleStackIcon },
  { name: '操作日志', href: '/logs', icon: Cog6ToothIcon },
  { name: '系统调试', href: '/system', icon: ExclamationTriangleIcon },
];

function MouseGlowEffect() {
  const { mousePosition } = useElasticSheet();

  if (!mousePosition) return null;

  return (
    <div className="fixed inset-0 z-0 pointer-events-none">
      {/* 主要光晕 - 快速响应 */}
      <div 
        className="pointer-events-none absolute -inset-px transition-opacity duration-300"
        style={{
          opacity: 0.35,
          background: `radial-gradient(400px circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(96, 165, 250, 0.075), transparent 45%)`,
        }}
      />
      
      {/* 次要光晕 - 中等延迟 */}
      <div 
        className="pointer-events-none absolute -inset-px transition-opacity duration-500"
        style={{
          opacity: 0.2,
          background: `radial-gradient(600px circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(139, 92, 246, 0.06), transparent 50%)`,
        }}
      />
      
      {/* 最外层光晕 - 最大延迟 */}
      <div 
        className="pointer-events-none absolute -inset-px transition-opacity duration-700"
        style={{
          opacity: 0.1,
          background: `radial-gradient(800px circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(59, 130, 246, 0.04), transparent 55%)`,
        }}
      />
    </div>
  );
}

export default function MainLayout() {
  const location = useLocation();
  const { adminId, logout } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });

  const toggleCollapsed = () => {
    const newValue = !collapsed;
    setCollapsed(newValue);
    localStorage.setItem('sidebar-collapsed', String(newValue));
  };

  const handleLogout = async () => {
    logout();
    window.location.href = '/login';
  };

  const SidebarContent = ({ isCollapsed = false }: { isCollapsed?: boolean }) => (
    <div className="flex flex-col h-full">
      <div className="flex items-center h-16 px-6">
        <div className="flex items-center space-x-3 w-full">
          <div className="w-8 h-8 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
            <ServerIcon className="w-5 h-5 text-white/80" />
          </div>
          <div className={`transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] overflow-hidden ${isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'}`}>
            <h1 className="text-xl font-bold tracking-tight text-white whitespace-nowrap">
              Drive Admin
            </h1>
          </div>
        </div>
      </div>

      <nav className="flex-1 py-6 px-4 space-y-1 overflow-y-auto">
        {navigation.map((item) => {
          const isActive = location.pathname === item.href;
          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              title={isCollapsed ? item.name : undefined}
              className={`flex items-center py-3 text-sm font-medium rounded-xl transition-all duration-200 ${
                isCollapsed ? 'px-3 justify-center' : 'px-4'
              } ${
                isActive
                  ? 'bg-white/10 text-white'
                  : 'text-white/50 hover:bg-white/5 hover:text-white'
              }`}
            >
              <item.icon
                className={`w-5 h-5 transition-colors flex-shrink-0 ${isCollapsed ? '' : 'mr-3'} ${isActive ? 'text-white' : ''}`}
                aria-hidden="true"
              />
              <div className={`transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] overflow-hidden ${isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'}`}>
                <span className="whitespace-nowrap">{item.name}</span>
              </div>
            </Link>
          );
        })}
      </nav>

      <div className="p-4">
        <Menu as="div" className="relative">
          <MenuButton className={`flex items-center w-full py-2.5 text-sm font-medium text-white/60 rounded-xl hover:bg-white/5 transition-all outline-none ${isCollapsed ? 'px-3 justify-center' : 'px-4'}`}>
            <UserCircleIcon className={`w-6 h-6 text-white/40 flex-shrink-0`} />
            <div className={`transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] overflow-hidden ${isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'}`}>
              <span className="truncate text-left">{adminId || '管理员'}</span>
            </div>
          </MenuButton>

          <MenuItems
            transition
            anchor={isCollapsed ? 'right end' : 'top'}
            className={`rounded-xl bg-white/[0.02] backdrop-blur-xl shadow-2xl focus:outline-none overflow-hidden z-50 transition ease-out duration-100 data-[closed]:ease-in data-[closed]:duration-75 data-[closed]:opacity-0 data-[closed]:scale-95 ${
              isCollapsed ? 'w-48 ml-3' : 'w-[var(--button-width)] mb-2'
            }`}
          >
            <div className="p-1">
              <MenuItem>
                {({ focus }) => (
                  <button
                    onClick={handleLogout}
                    className={`w-full flex items-center px-4 py-2.5 text-sm rounded-lg transition-colors ${
                      focus ? 'bg-white/[0.08] text-red-400' : 'text-red-400'
                    }`}
                  >
                    <ArrowRightOnRectangleIcon className="w-5 h-5 mr-3" />
                    退出登录
                  </button>
                )}
              </MenuItem>
            </div>
          </MenuItems>
        </Menu>
      </div>

      {/* Collapse Toggle - Desktop Only */}
      {isCollapsed !== undefined && (
        <div className={`${isCollapsed ? 'p-2' : 'p-4'} hidden lg:block`}>
          <button
            onClick={toggleCollapsed}
            className={`flex items-center w-full py-2.5 text-sm font-medium text-white/40 rounded-xl hover:bg-white/5 hover:text-white/70 transition-all ${isCollapsed ? 'px-3 justify-center' : 'px-4'}`}
            title={isCollapsed ? '展开侧边栏' : '收起侧边栏'}
          >
            {isCollapsed ? (
              <ChevronDoubleRightIcon className="w-5 h-5 flex-shrink-0" />
            ) : (
              <>
                <ChevronDoubleLeftIcon className="w-5 h-5 flex-shrink-0 mr-3" />
                <span>收起菜单</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <ElasticSheetProvider>
      <div className="relative min-h-screen bg-black selection:bg-blue-500/30">
        {/* Mouse Follow Glow Effect */}
        <MouseGlowEffect />
        
        {/* Ambient Light Background */}
        <div className="fixed inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-[40rem] h-[40rem] bg-blue-600/20 rounded-full blur-[128px] opacity-50"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-[40rem] h-[40rem] bg-purple-600/20 rounded-full blur-[128px] opacity-50"></div>
        </div>

      {/* Sidebar background gradient */}
      <div className="fixed inset-0 z-[-1] pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/[0.02] via-transparent to-white/[0.02] opacity-50" />
      </div>

      {/* Mobile Sidebar */}
      <Transition show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
          <TransitionChild
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          </TransitionChild>

          <div className="fixed inset-0 flex">
            <TransitionChild
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <DialogPanel className="relative flex w-full max-w-xs flex-1">
                <div className="flex grow flex-col bg-white/[0.02] backdrop-blur-xl h-full">
                  <div className="flex items-center h-16 px-4">
                    <div className="flex items-center gap-x-3">
                      <div className="w-8 h-8 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                        <ServerIcon className="w-5 h-5 text-white/80" />
                      </div>
                      <h1 className="text-lg font-bold tracking-tight text-white">Drive Admin</h1>
                    </div>
                    <button
                      type="button"
                      className="ml-auto p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-all"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <XMarkIcon className="h-5 w-5 text-white/70" />
                    </button>
                  </div>
                  <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
                    {navigation.map((item) => {
                      const isActive = location.pathname === item.href;
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={() => setSidebarOpen(false)}
                          className={`flex items-center px-3 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 ${
                            isActive
                              ? 'bg-white/10 text-white'
                              : 'text-white/50 hover:bg-white/5 hover:text-white'
                          }`}
                        >
                          <item.icon className={`w-5 h-5 mr-3 ${isActive ? 'text-white' : ''}`} />
                          {item.name}
                        </Link>
                      );
                    })}
                  </nav>
                  <div className="p-4">
                    <Menu as="div" className="relative">
                      <MenuButton className="flex items-center w-full px-3 py-2.5 text-sm font-medium text-white/60 rounded-xl hover:bg-white/5 transition-all outline-none">
                        <UserCircleIcon className="w-6 h-6 text-white/40 mr-3" />
                        <span className="truncate flex-1 text-left">{adminId || '管理员'}</span>
                      </MenuButton>
                      <MenuItems className="absolute bottom-full mb-2 left-0 right-0 rounded-xl bg-white/[0.02] backdrop-blur-xl shadow-2xl overflow-hidden">
                        <div className="p-1">
                          <MenuItem>
                            {({ focus }) => (
                              <button
                                onClick={handleLogout}
                                className={`w-full flex items-center px-4 py-2.5 text-sm rounded-lg transition-colors ${
                                  focus ? 'bg-white/[0.08] text-red-400' : 'text-red-400'
                                }`}
                              >
                                <ArrowRightOnRectangleIcon className="w-5 h-5 mr-3" />
                                退出登录
                              </button>
                            )}
                          </MenuItem>
                        </div>
                      </MenuItems>
                    </Menu>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>

      {/* Static Sidebar for Desktop */}
      <div className={`hidden lg:fixed lg:inset-y-0 lg:z-10 lg:flex lg:flex-col transition-all duration-300 ${collapsed ? 'lg:w-16' : 'lg:w-64'}`}>
        <div className="flex grow flex-col bg-white/[0.02] backdrop-blur-xl overflow-y-auto">
          <SidebarContent isCollapsed={collapsed} />
        </div>
      </div>

      {/* Main Content Area */}
      <div className={`flex flex-col min-h-screen transition-all duration-300 ${collapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
        {/* Top Header for Mobile */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 bg-zinc-900/40 backdrop-blur-md px-4 ring-1 ring-white/10 sm:gap-x-6 sm:px-6 lg:hidden" style={{ height: '4rem' }}>
          <button
            type="button"
            className="-m-2.5 p-2.5 text-white/70 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex items-center gap-x-3">
              <div className="w-8 h-8 rounded-xl bg-white/10 flex items-center justify-center">
                <ServerIcon className="w-5 h-5 text-white/80" />
              </div>
              <span className="text-lg font-bold tracking-tight text-white">Drive Admin</span>
            </div>
          </div>
        </div>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-screen-2xl w-full mx-auto relative z-10">
          <Outlet />
        </main>
      </div>
      </div>
    </ElasticSheetProvider>
  );
}